import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { getSkilledNeedDescription } from 'components/Assessments/constants/skilledNeedsDescriptions'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'
import { SkilledNeed } from './formData'
import { SkilledNeedsFormField } from './formField'

/**
 * Skilled Needs subsection adapter for SOC Assessment
 * Handles skilled needs extraction and transformation
 */
export class SkilledNeedsAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const skilledNeedsAnswers =
      flatAnswers[SocAssessmentQuestionnaireItem.SKILLED_NEEDS_NEED]?.answer ?? []
    const skilledNeedsFormData = skilledNeedsAnswers.map<SkilledNeed>((skilledNeedAnswerItem) => {
      const skilledNeedInfoItem = skilledNeedAnswerItem.item ?? []
      const name = getAnswerValuesFromItems(
        skilledNeedInfoItem,
        SocAssessmentQuestionnaireItem.SKILLED_NEEDS_NEED_NAME
      )?.at(0)

      const score = getAnswerValuesFromItems(
        skilledNeedInfoItem,
        SocAssessmentQuestionnaireItem.SKILLED_NEEDS_NEED_SCORE
      )?.at(0)

      const note = getAnswerValuesFromItems(
        skilledNeedInfoItem,
        SocAssessmentQuestionnaireItem.SKILLED_NEEDS_NEED_NOTE
      )?.at(0)

      const previousNote = getAnswerValuesFromItems(
        skilledNeedInfoItem,
        SocAssessmentQuestionnaireItem.SKILLED_NEEDS_NEED_PREVIOUS_NOTE
      )?.at(0)

      // Description is derived from name and score, not stored directly
      const dynamicDescription = getSkilledNeedDescription(name, score)
      const description = dynamicDescription ? `${score} - ${dynamicDescription}` : 'unknown'

      return {
        [SkilledNeedsFormField.SKILLED_NEED_NAME]: name,
        [SkilledNeedsFormField.SKILLED_NEED_SCORE]: score,
        [SkilledNeedsFormField.SKILLED_NEED_NOTE]: note,
        [SkilledNeedsFormField.SKILLED_NEED_PREVIOUS_NOTE]: previousNote,
        [SkilledNeedsFormField.SKILLED_NEED_DESCRIPTION]: description
      }
    })
    return {
      [SkilledNeedsFormField.SKILLED_NEED]: skilledNeedsFormData
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []
    // Skilled needs section is read-only, so we don't build mutations
    // But we check if the field exists to follow the pattern
    if (this.formDataFieldExists(SkilledNeedsFormField.SKILLED_NEED)) {
      // Field exists but we don't save changes (read-only section)
      // Access the field to follow the pattern, even though we don't use it
      const _skilledNeeds = this.getFormDataField(SkilledNeedsFormField.SKILLED_NEED)
      // If needed in the future, we could build mutations here using the skilledNeeds data
    }
    return mutations
  }
}
